import { Checkbox, FormControl, FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material';
import { memo, useCallback } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import TypeIndentityCard from 'shell/components/shared/type-indentity-card';
import { FormikErrors, FormikTouched } from 'formik';
import { AccordionIdentityCardVM } from 'front-office/view-models/registration-id-card';
import ReleaseModeIndentityCard from '../../../../shell/components/shared/release-mode';
// import TypePaymentList from 'front-office/components/shared/payment/type-payment';
import ConsulateRelease from './consulate-release';
import MunicipalityRelease from './municipality-release';
import { PmiTooltipError } from 'shell/components/shared/tooltip-error';
import { DateTime } from 'luxon';
import { noop } from 'shell/utils/noop';

interface AccordionRegistrationIdentityCardFormProps {
  disabled?: boolean;
  errors?: FormikErrors<AccordionIdentityCardVM>;
  touched?: FormikTouched<AccordionIdentityCardVM>;
  values?: AccordionIdentityCardVM;
  onChange: (
    key: keyof AccordionIdentityCardVM,
    value: AccordionIdentityCardVM[keyof AccordionIdentityCardVM]
  ) => void;
  onChangeValues: (values: AccordionIdentityCardVM) => void;
  birthday: DateTime | null;
}

const AccordionRegistrationIdentityCardFormComponent = ({
  values,
  touched,
  errors,
  onChange,
  birthday,
  onChangeValues,
}: AccordionRegistrationIdentityCardFormProps): React.ReactElement => {
  const isLeapYear = (year: number): boolean => {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  };

  const calculateNewExpirationDate = (
    birthDate: DateTime,
    currentDate: DateTime,
    isAsylum: boolean
  ) => {
    const birthMonth = birthDate.get('month');
    const birthDay = birthDate.get('day');
    const birthYear = birthDate.get('year');

    const currentYear = currentDate.get('year');
    const currentMonth = currentDate.get('month');
    const currentDay = currentDate.get('day');

    let expiryYear, expiryMonth, expiryDay;

    if (isAsylum) {
      expiryYear = currentYear + 3;

      if (birthMonth < currentMonth || (birthMonth === currentMonth && birthDay < currentDay)) {
        expiryYear += 1;
      }

      expiryMonth = birthMonth;
      expiryDay = birthDay;
    } else {
      let age = currentYear - birthYear;
      if (birthMonth > currentMonth || (birthMonth === currentMonth && birthDay > currentDay)) {
        age -= 1;
      }

      if (age < 3) {
        expiryYear = currentYear + 3;
      } else if (age >= 3 && age < 18) {
        expiryYear = currentYear + 5;
      } else {
        expiryYear = currentYear + 9;
      }

      if (birthMonth < currentMonth || (birthMonth === currentMonth && birthDay < currentDay)) {
        expiryYear += 1;
      }

      expiryMonth = birthMonth;
      expiryDay = birthDay;
    }

    if (!isLeapYear(expiryYear) && expiryDay === 29 && expiryMonth === 2) {
      expiryDay = 1;
      expiryMonth = 3;
    }

    return DateTime.fromObject({
      year: expiryYear,
      month: expiryMonth,
      day: expiryDay,
    });
  };

  const handleReleaseDateChange = (
    dataValue: DateTime | null,
    isAsylum: boolean = values?.isRequestedAsylum || false
  ) => {
    if (birthday && dataValue) {
      const newExpirationDate = dataValue
        ? calculateNewExpirationDate(birthday, dataValue, isAsylum)
        : null;
      onChangeValues({
        releaseDate: dataValue,
        expirationDate: newExpirationDate,
        isRequestedAsylum: isAsylum,
      });
    }
  };

  const handleChangeType = useCallback(
    (type) => {
      onChange('type', type);
    },
    [onChange]
  );

  const handleChangeReleaseMode = useCallback(
    (releaseMode) => {
      onChange('releaseMode', releaseMode);
    },
    [onChange]
  );

  // const handleChangeTypePayment = useCallback(
  //   (typePayment) => {
  //     onChange('typePayment', typePayment);
  //   },
  //   [onChange]
  // );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="Numero *"
          aria-label="Numero"
          fullWidth
          onChange={(event) => {
            onChange('number', event?.target.value);
          }}
          inputProps={{
            maxLength: '9',
          }}
          value={values?.number || ''}
          error={touched?.number && Boolean(errors?.number)}
          helperText={errors?.number}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTooltipError message={errors?.type} show={touched?.type && Boolean(errors?.type)}>
          <TypeIndentityCard
            label="Tipo *"
            value={values?.type || ''}
            onChange={handleChangeType}
            error={touched?.type && Boolean(errors?.type)}
          />
        </PmiTooltipError>
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControlLabel
          control={
            <Checkbox
              id="expatriationBan"
              checked={values?.expatriationBan || false}
              onChange={() => onChange('expatriationBan', !values?.expatriationBan)}
              inputProps={{ 'aria-label': 'Interdizione espatrio *' }}
            />
          }
          label="Interdizione espatrio"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControlLabel
          control={
            <Checkbox
              id="isRequestedAsylum"
              checked={values?.isRequestedAsylum || false}
              onChange={() => {
                const newIsRequestedAsylum = !values?.isRequestedAsylum;
                handleReleaseDateChange(values?.releaseDate ?? null, newIsRequestedAsylum);
              }}
              inputProps={{ 'aria-label': 'Richiedente asilo' }}
            />
          }
          label="Richiedente asilo"
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data rilascio"
          value={values?.releaseDate || null}
          onChange={(dataValue) => handleReleaseDateChange(dataValue)}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              fullWidth
              helperText={errors?.releaseDate}
              error={touched?.releaseDate && Boolean(errors?.releaseDate)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data scadenza *"
          value={values?.expirationDate || null}
          disabled
          onChange={noop}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              fullWidth
              helperText={errors?.expirationDate}
              error={touched?.expirationDate && Boolean(errors?.expirationDate)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}></Grid>
      <Grid item xs={12} md={5}>
        <PmiTooltipError
          message={errors?.releaseMode}
          show={touched?.releaseMode && Boolean(errors?.releaseMode)}
        >
          <ReleaseModeIndentityCard
            required
            value={values?.releaseMode || ''}
            onChange={handleChangeReleaseMode}
            error={touched?.releaseMode && Boolean(errors?.releaseMode)}
          />
        </PmiTooltipError>
      </Grid>
      <Grid item xs={12} md={3} display="flex" alignSelf="center">
        {/* <FormControlLabel
          control={
            <Checkbox
              id="toAccount"
              checked={values?.toAccount || false}
              disabled={
                !(
                  values?.type === '3' &&
                  values.releaseDate?.get('year') === DateTime.local().get('year')
                )
              }
              onChange={() => onChange('toAccount', !values?.toAccount)}
              inputProps={{ 'aria-label': 'da Contabilizzare' }}
            />
          }
          label="da Contabilizzare"
        /> */}
      </Grid>
      <Grid item xs={12} md={4}>
        {/* <PmiTooltipError
          message={errors?.releaseMode}
          show={touched?.releaseMode && Boolean(errors?.releaseMode)}
        >
          <TypePaymentList
            required
            value={values?.typePayment || ''}
            error={touched?.typePayment && Boolean(errors?.typePayment)}
            onChange={(event) => {
              handleChangeTypePayment(event?.target.value);
            }}
          />
        </PmiTooltipError> */}
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Rilascio *"
            id="isConsulateRelease"
            defaultValue={false}
            value={values?.isConsulateRelease}
            onChange={() => onChange('isConsulateRelease', !values?.isConsulateRelease)}
          >
            <FormControlLabel value="false" control={<Radio />} label="Comune rilascio" />
            <FormControlLabel value="true" control={<Radio />} label="Consolato rilascio" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {values?.isConsulateRelease ? (
        <ConsulateRelease values={values} onChange={onChange} errors={errors} touched={touched} />
      ) : (
        <MunicipalityRelease
          values={values}
          onChange={onChange}
          errors={errors}
          touched={touched}
        />
      )}
    </Grid>
  );
};

AccordionRegistrationIdentityCardFormComponent.displayName =
  'Accordion registration id card form component';

const AccordionRegistrationIdentityCardForm = memo(AccordionRegistrationIdentityCardFormComponent);

export default AccordionRegistrationIdentityCardForm;
